<template>
    <form
        action="url"
        method="post"
        @submit.prevent="submit"
    >
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="row-start-2 sm:row-auto space-y-8">
                <div class="space-y-4">
                    <heading-2 class="text-gray-800">Delivery Address</heading-2>

                    <div class="space-y-2">

                        <text-input
                            name="address"
                            placeholder="Street Address"
                            type="text"
                            ref="address"
                            :error="error('address')"
                            :error-message="false"
                        />
                    </div>
                    <small v-if="addressErrorMessage" class="form-error">{{ addressErrorMessage }}</small>
                </div>
                <div class="py-4 space-y-4 border-t-2 border-b-2 font-semibold text-gray-500 leading-7">
                    <p>Once you confirm your request through PayPal, {{ profile.name }} will be notificed and will contact you to schedule your delivery.</p>
                    <p>You will not be charged until {{ profile.name }} confirms the delivery date.</p>
                </div>
                <div class="">
                    <button
                        class="btn btn-green w-full"
                        :class="{ 'bg-green-600 cursor-not-allowed': submitted }"
                        :disabled="submitted"
                    >
                        PayPal
                    </button>
                </div>
            </div>

            <div class="">
                <div class="-mt-4 pt-4 sticky top-0">
                    <div class="border rounded-lg p-4 space-y-4">
                        <div class="flex space-x-4">
                            <div class="w-1/3">
                                <div class="relative h-0 rounded-lg overflow-hidden bg-gray-200" style="padding-bottom: 100%;">
                                        <img v-if="profile.photoUrl" class="absolute inset-0" :src="profile.photoUrl" :alt="profile.name">
                                        <div v-else class="absolute inset-0 w-full h-full flex justify-center items-center p-8">
                                            <icon type="photograph" class="w-full h-full text-gray-100" />
                                        </div>
                                </div>
                            </div>
                            <div class="flex-1">
                                <div class="text-lg text-gray-800 font-semibold">{{ profile.name }}</div>
                                <div class="text-gray-700 font-semibold">{{ profile.market.city }}, {{ profile.market.short_state }}</div>
                            </div>
                        </div>
                        <div>
                            <div class="space-y-2">
                                <heading-2 class="text-gray-800">Order:</heading-2>
                                <div>
                                    <div class="divide-y">
                                        <heading-3 class="text-center text-gray-700">Sizes</heading-3>
                                        <div class="pb-4 space-y-1">
                                            <label
                                                v-for="(sizePrice, index) in sizePrices"
                                                :key="index"
                                                class="p-2 flex space-x-4 items-center rounded hover:bg-gray-200"
                                            >
                                                <div class="flex-1 text-lg font-semibold text-gray-600">{{ sizePrice.name }}:</div>
                                                <div class="text-xl font-bold text-gray-800 text-right">${{ sizePrice.amount }}</div>
                                                <input type="radio" name="items[]" v-model="size" :value="sizePrice.name">
                                            </label>
                                            <small v-if="error('size')" class="form-error">{{ error('size') }}</small>
                                        </div>
                                    </div>
                                    <div v-if="addOnPrices.length > 0" class="divide-y">
                                        <heading-3 class="text-center text-gray-700">Add Ons</heading-3>
                                        <div class="py-2 space-y-1">
                                            <label
                                                v-for="(addOnPrice, index) in addOnPrices"
                                                :key="index"
                                                class="p-2 flex space-x-4 items-center rounded hover:bg-gray-200"
                                            >
                                                <div class="flex-1 text-lg font-semibold text-gray-600">{{ addOnPrice.name }}:</div>
                                                <div class="text-xl font-bold text-gray-800 text-right">${{ addOnPrice.amount }}</div>
                                                <input type="checkbox" name="items[]" v-model="addOns" :value="addOnPrice.name">
                                            </label>
                                        </div>
                                    </div>
                                    <div class="border-t">
                                        <div class="p-2 flex space-x-4 items-center rounded">
                                            <div class="flex-1 text-lg font-semibold text-gray-600">Delivery Fee:</div>
                                            <div class="text-xl font-bold text-gray-800 text-right">${{ delivery.amount }}</div>
                                            <input type="hidden" name="items[]" value="delivery">
                                        </div>
                                    </div>
                                    <div class="border-t-2">
                                        <div class="p-2 flex space-x-4 items-center rounded">
                                            <div class="flex-1 text-lg font-bold text-gray-700 uppercase">Total:</div>
                                            <div class="text-xl font-bold text-gray-800 text-right">${{ total }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import Heading3 from './shared/Heading3'
import Heading2 from './shared/Heading2'
import Icon from './shared/Icon'
import TextInput from './shared/TextInput'
import { get } from 'lodash'
import pluralize from "pluralize";

export default {
    props: {
        initialAddress: Object,
        initialSize: String,
        initialAddOns: {
            type: Array,
            default() {
                return [];
            }
        },
        url: {
            type: String,
            required: true,
        },
        profile: {
            type: Object,
            required: true
        },
        prices: {
            type: Array,
            required: true,
        },
        merchant: {
            type: Object,
            required: true,
        }
    },
    data() {
        return {
            submitted: false,
            address: {
                street_1: get(this.initialAddress, 'street_1', null),
                street_2: get(this.initialAddress, 'street_2', null),
                city: get(this.initialAddress, 'city', null),
                state: get(this.initialAddress, 'state', null),
                postal_code: get(this.initialAddress, 'postal_code', null),
            },
            size: this.initialSize,
            addOns: this.initialAddOns,
            errors: {}
        }
    },
    computed: {
        form() {
            return {
                address: this.address,
                size: this.size,
                add_ons: this.addOns,
                merchant: this.merchant.id,
            }
        },
        sizePrices() {
            return this.prices.filter(price => price.type == 'size')
        },
        addOnPrices() {
            return this.prices.filter(price => price.type == 'add_on')
        },
        delivery() {
            return this.prices.find(price => price.type == 'delivery')
        },
        total() {
            return _.chain(this.prices).filter(price => {
                return _.some(this.items, price.name)
                    || price.type == 'delivery'
            })
            .sumBy('amount')
            .value()
        },
        addressErrorMessage() {
            return _.chain(this.errors)
                    .filter((error, index) => _.startsWith(index, 'address'))
                    .flattenDeep()
                    .first()
                    .value()
        }
    },
    watch: {
        form: {
            deep: true,
            handler(form) {
                updateQueryString(_.cloneDeep(form))
            }
        }
    },
    methods: {
        error(key) {
            return _.chain(this.errors).get(key).first().value()
        },
        submit() {
            this.submitted
            window.axios.post(this.url, this.form)
            .then(res => alert('success'))
            .catch(err => {
                if(get(err, 'response.status') == 422) {
                    this.errors = get(err, 'response.data.errors')
                    this.sendErrorNotification()
                }
            })
            .then(() => this.submitted = false)
        },
        sendErrorNotification() {
            emitter.emit('show-notification', {
                type: 'danger',
                text: `Your request has ${pluralize('errors', _.size(this.errors), true)}.`
            })
        }
    },
    mounted() {
        let autocomplete = new google.maps.places.Autocomplete(this.$refs.address.$refs.input, {
            componentRestrictions: { country: ["us"] },
            fields: ["address_components", 'geometry'],
            types: ["address"],
        })
        autocomplete.addListener('place_changed', () => {
            let place = autocomplete.getPlace()
            console.log(place)
            let number = _.chain(place.address_components).find({types: ['street_number']}).get('short_name').value()
            let street = _.chain(place.address_components).find({types: ['route']}).get('short_name').value()
            this.address.street_1 = `${number} ${street}`
            this.address.city = _.chain(place.address_components).find({types: ['administrative_area_level_3']}).get('short_name').value()
            this.address.state = _.chain(place.address_components).find({types: ['administrative_area_level_1']}).get('short_name').value()
            this.address.postal_code = _.chain(place.address_components).find({types: ['postal_code']}).get('short_name').value()

            this.address.lat = place.geometry.location.lat()
            this.address.long = place.geometry.location.lng()
        })
    },
    components: {
        TextInput,
        Icon,
        Heading2,
        Heading3,
    }
}
</script>
